import type { InvoiceTransactionDetail } from 'ap-openapi';
import { useMemo } from 'react';

import type { CardColumn } from '@/components/CardTable';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import {
  getDefaultColumnProps,
  getDynamicColumnProps,
  isCustomTransactionKeyExists,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/helpers/invoiceTransactionColumns';
import { TransactionProperty } from '@/features/InvoiceReport/Edit/type';
import { TRANSACTION_MASTER_FIELD_NAMES } from '@/features/transaction_items/constants';
import {
  batchOptionsToNestedLabelMaps,
  useCustomTransactionPulldownLabels,
} from '@/hooks/useGetCustomTransactionPulldownLabels';
import { useTranslation } from '@/i18n';
import { useColumns } from '../Table/Columns/columns';

// `id` is optional in the auto-generated `InvoiceTransactionDetail` type.
// But it is required for the table, so make it explicitly required here.
export type InvoiceTransactionRow = InvoiceTransactionDetail &
  Required<Pick<InvoiceTransactionDetail, 'id'>>;

export type InvoiceTransactionColumn = CardColumn<InvoiceTransactionRow>;

export const useInvoiceTransactionColumns = (
  reportId: string,
  invoiceTransactions: InvoiceTransactionDetail[],
  dynamicTransactionFields: TransactionProperty[],
  showCurrencyCell: boolean = false
): InvoiceTransactionColumn[] => {
  const customTransactionItemIdsExists = useMemo(
    () => isCustomTransactionKeyExists(invoiceTransactions),
    [invoiceTransactions]
  );

  const { data: pulldownLabels, isLoading: isLoadingPulldownLabels } =
    useCustomTransactionPulldownLabels(
      invoiceTransactions,
      dynamicTransactionFields
    );
  const pulldownLabelsMap = useMemo(
    () => batchOptionsToNestedLabelMaps(pulldownLabels ?? []),
    [pulldownLabels]
  );

  const { currentOffice } = useLoginInfo();

  // Default columns are hard-coded
  const defaultColumns = useColumns(showCurrencyCell);
  const { t } = useTranslation();

  return useMemo(() => {
    const columns: InvoiceTransactionColumn[] = [];

    // If the array is empty, that means the dynamic columns haven't been defined.
    // If the dynamic columns aren't defined, use the default columns as-is
    // (this will happen if user hasn't visited the config page yet)
    if (dynamicTransactionFields.length === 0) {
      return defaultColumns;
    }

    // Column that exists in defaults, but not in the API response
    const fixedColumn = getDefaultColumnProps(
      defaultColumns,
      TRANSACTION_MASTER_FIELD_NAMES.NUMBER
    );
    if (fixedColumn) {
      columns.push(fixedColumn);
    }

    // Dynamic columns generated by merging API result with default columns
    dynamicTransactionFields.forEach((dynamicFieldProps, index) => {
      // If the custom transaction item is not visible and has no value, skip the column
      const isCustomItemExistsInData = customTransactionItemIdsExists.has(
        dynamicFieldProps.id
      );

      const isHiddenWithNoValue =
        (!isCustomItemExistsInData && !dynamicFieldProps.isVisible) ||
        (dynamicFieldProps.type === 'custom' &&
          !currentOffice?.isMoreThanTeamPlan &&
          !isCustomItemExistsInData);

      if (isHiddenWithNoValue) return;

      const columnProps = getDynamicColumnProps(
        defaultColumns,
        dynamicFieldProps,
        t,
        index === dynamicTransactionFields.length - 1,
        pulldownLabelsMap,
        isLoadingPulldownLabels
      );
      if (columnProps) {
        columns.push(columnProps);
      }
    });

    return columns;
  }, [
    dynamicTransactionFields,
    defaultColumns,
    customTransactionItemIdsExists,
    currentOffice?.isMoreThanTeamPlan,
    t,
    pulldownLabelsMap,
    isLoadingPulldownLabels,
  ]);
};
